import type { HsDocumentView, HsProcessingView, HsTransport, HsTransportRequest } from '@sibe/hyperscene';
import { hsEnsureOk } from '../../../shared/http/api-error.function';

const JSON_CONTENT_TYPE: string = 'application/json';

export interface HsIngestApiOptions {
  transport: HsTransport;
}

/** True for processing states that will not change any more. */
export function hsIsTerminalProcessingStatus(status: string | null | undefined): boolean {
  return status === 'Completed' || status === 'Failed' || status === 'Canceled';
}

export interface HsWaitForProcessingOptions {
  /** Delay between polls (default 1500 ms) and the number of polls before giving up (default 120). */
  intervalMs?: number;
  attempts?: number;
  signal?: AbortSignal;
  /** Called with every polled state, for progress display. */
  onUpdate?: (processing: HsProcessingView) => void;
}

/** Uploads source files and runs the processings that turn them into models. */
export class HsIngestApi {
  private readonly transport: HsTransport;

  public constructor(options: HsIngestApiOptions) {
    this.transport = options.transport;
  }

  public uploadDocument(file: Blob, fileName: string): Promise<HsDocumentView> {
    const form: FormData = new FormData();
    form.append('file', file, fileName);
    return this.json<HsDocumentView>({ path: 'api/Documents', method: 'POST', body: form });
  }

  /** Starts parsing uploaded documents and returns the processing id. */
  public parseDocument(request: unknown): Promise<string> {
    return this.json<string>({ path: 'api/Documents/Parse', method: 'POST', body: request });
  }

  /** Parses a model's source documents again and returns the processing id. */
  public reparseModel(modelId: string): Promise<string> {
    return this.json<string>({
      path: `api/Models/${encodeURIComponent(modelId)}/Reparse`,
      method: 'POST',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      headers: { 'Content-Type': JSON_CONTENT_TYPE },
    });
  }

  public getProcessing(processingId: string): Promise<HsProcessingView> {
    return this.json<HsProcessingView>({ path: `api/Processings/${encodeURIComponent(processingId)}` });
  }

  public filterProcessings(metadata: unknown[]): Promise<HsProcessingView[]> {
    return this.json<HsProcessingView[]>({ path: 'api/Processings/Filter', method: 'POST', body: metadata });
  }

  public async deleteProcessings(processingIds: readonly string[]): Promise<void> {
    await hsEnsureOk(
      await this.transport.request({
        path: 'api/Processings/Bulk',
        method: 'DELETE',
        accept: [JSON_CONTENT_TYPE],
        body: processingIds,
      })
    );
  }

  /** Polls a processing until it is completed, failed or canceled; rejects on timeout or abort. */
  public async waitForProcessing(
    processingId: string,
    options: HsWaitForProcessingOptions = {}
  ): Promise<HsProcessingView> {
    const attempts: number = options.attempts ?? 120;
    const intervalMs: number = options.intervalMs ?? 1500;
    for (let attempt: number = 0; attempt < attempts; attempt++) {
      if (options.signal?.aborted === true) {
        break;
      }
      const processing: HsProcessingView = await this.getProcessing(processingId);
      options.onUpdate?.(processing);
      if (hsIsTerminalProcessingStatus(processing.status)) {
        return processing;
      }
      await new Promise<void>((resolve: () => void) => setTimeout(resolve, intervalMs));
    }
    throw new Error('Processing polling timed out or was canceled.');
  }

  private async json<T>(request: HsTransportRequest): Promise<T> {
    return (await hsEnsureOk(await this.transport.request({ ...request, accept: [JSON_CONTENT_TYPE] }))).json<T>();
  }
}
